import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { otrosTramitesService } from '../services/otrosTramitesService';
import type { ArchivoRecord } from '../types';

interface UseArchivosProps {
  otroTramiteId: string;
  scope?: 'all' | 'factura' | 'otros';
  tipoUpload?: 'factura';
}

export const useArchivos = ({
  otroTramiteId,
  scope = 'all',
  tipoUpload,
}: UseArchivosProps) => {
  const queryClient = useQueryClient();

  // Query to fetch archivos
  const {
    data: archivosData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['otros-tramites-archivos', otroTramiteId, scope],
    queryFn: () => otrosTramitesService.listArchivos(otroTramiteId, scope),
    enabled: !!otroTramiteId,
    refetchOnWindowFocus: false,
  });

  const archivos = archivosData?.data || [];
  const totalArchivos = archivosData?.total || 0;

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const validation = validateFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }
      return otrosTramitesService.uploadArchivo(otroTramiteId, file, tipoUpload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['otros-tramites-archivos', otroTramiteId],
      });
      // Also invalidate tramites list to update archivo count
      queryClient.invalidateQueries({
        queryKey: ['otros-tramites'],
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (archivoId: number) => otrosTramitesService.deleteArchivo(archivoId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['otros-tramites-archivos', otroTramiteId],
      });
      queryClient.invalidateQueries({
        queryKey: ['otros-tramites'],
      });
    },
  });

  // Upload file
  const uploadArchivo = useCallback(
    async (file: File) => {
      return uploadMutation.mutateAsync(file);
    },
    [uploadMutation]
  );

  // Delete archivo
  const deleteArchivo = useCallback(
    async (archivoId: number) => {
      return deleteMutation.mutateAsync(archivoId);
    },
    [deleteMutation]
  );

  // Download archivo
  const downloadArchivo = useCallback(
    async (archivo: ArchivoRecord) => {
      try {
        await otrosTramitesService.downloadArchivo(archivo);
      } catch (error) {
        console.error('Error downloading archivo:', error);
        throw error;
      }
    },
    []
  );

  // Validate file
  const validateFile = useCallback((file: File): { valid: boolean; error?: string } => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'El archivo excede el tamaño máximo de 10MB',
      };
    }

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Solo se permiten archivos PDF, JPG o PNG',
      };
    }

    return { valid: true };
  }, []);

  // Get file icon based on mime type
  const getFileIcon = useCallback((mimeType: string) => {
    if (mimeType.includes('pdf')) return 'FileText';
    if (mimeType.includes('image')) return 'FileImage';
    return 'File';
  }, []);

  return {
    // Data
    archivos,
    totalArchivos,
    isLoading,
    error,

    // Actions
    uploadArchivo,
    deleteArchivo,
    downloadArchivo,
    refetch,

    // Mutation states
    isUploading: uploadMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Helpers
    validateFile,
    getFileIcon,
  };
};
